import { useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faTrashCan,
  faArrowsRotate,
} from "@fortawesome/pro-solid-svg-icons";
import DataTable from "@components/DataTable";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";
import {
  useListClientCredentials,
  useCreateClientCredential,
  useRotateClientCredential,
  useUpdateClientCredential,
  useDeleteClientCredential,
} from "./api";
import { ClientCredential } from "./types";
import CredentialFormModal from "./CredentialFormModal";
import RevealCredentialsModal from "./RevealCredentialsModal";
import RotateConfirmModal from "./RotateConfirmModal";
import DeleteConfirmModal from "./DeleteConfirmModal";
import "./styles.css";

interface Props {
  userId: number;
}

interface RevealState {
  clientSecret: string;
  clientId: string;
  clientIdMasked: boolean;
}

type FormState =
  | { mode: "create" }
  | { mode: "edit"; credential: ClientCredential };

const ClientCredentials = ({ userId }: Props): JSX.Element => {
  const { credentials, isLoading, refetch } = useListClientCredentials(userId);
  const { createCredential, isCreating } = useCreateClientCredential();
  const { rotateCredential, isRotating } = useRotateClientCredential();
  const { updateCredential, isUpdating } = useUpdateClientCredential();
  const { deleteCredential, isDeleting } = useDeleteClientCredential();

  const [form, setForm] = useState<FormState | null>(null);
  const [reveal, setReveal] = useState<RevealState | null>(null);
  const [rotateTarget, setRotateTarget] = useState<ClientCredential | null>(
    null
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);

  const showError = (err: any, fallback: string) => {
    const detail = err?.data?.errors?.[0]?.detail;
    setErrorMessage(detail || fallback);
    setShowFailAlert(true);
  };

  const selectedCredentials = credentials.filter((c) =>
    selectedIds.includes(String(c.id))
  );

  const handleSubmitForm = async (values: {
    name: string;
    redirectUri: string;
  }) => {
    if (!form) return;
    if (form.mode === "create") {
      try {
        const created = await createCredential({
          userId,
          name: values.name,
          redirectUri: values.redirectUri || undefined,
        });
        setForm(null);
        setReveal({
          clientId: created.clientId,
          clientSecret: created.clientSecret,
          clientIdMasked: false,
        });
        refetch();
      } catch (err: any) {
        showError(err, "Failed to create client credential.");
      }
    } else {
      try {
        await updateCredential({
          userId,
          credentialId: form.credential.id,
          name: values.name,
          redirectUri: values.redirectUri || undefined,
        });
        setForm(null);
        refetch();
      } catch (err: any) {
        showError(err, "Failed to update client credential.");
      }
    }
  };

  const handleConfirmRotate = async () => {
    if (!rotateTarget) return;
    try {
      const newSecret = await rotateCredential({
        userId,
        credentialId: rotateTarget.id,
      });
      const masked = rotateTarget.clientId;
      setRotateTarget(null);
      setReveal({
        clientId: masked,
        clientSecret: newSecret,
        clientIdMasked: true,
      });
      refetch();
    } catch (err: any) {
      setRotateTarget(null);
      showError(err, "Failed to rotate client secret.");
    }
  };

  const handleConfirmDelete = async () => {
    if (selectedCredentials.length === 0) return;
    try {
      await Promise.all(
        selectedCredentials.map((c) =>
          deleteCredential({ userId, credentialId: c.id })
        )
      );
      setShowDeleteConfirm(false);
      setSelectedIds([]);
      refetch();
    } catch (err: any) {
      setShowDeleteConfirm(false);
      showError(
        err,
        selectedCredentials.length > 1
          ? "One or more client credentials could not be deleted."
          : "Failed to delete client credential."
      );
    }
  };

  const handleCloseReveal = () => setReveal(null);

  const columns: ColumnDef<ClientCredential>[] = [
    {
      header: "Name",
      cell: (props) => {
        const row = props.row.original;
        return (
          <div
            className="editLink"
            onClick={() => setForm({ mode: "edit", credential: row })}
          >
            {row.name} <FontAwesomeIcon icon={faPencil} />
          </div>
        );
      },
    },
    {
      header: "Redirect URI",
      cell: (props) => props.row.original.redirectUri || "—",
    },
    {
      header: "Client ID",
      accessorKey: "clientId",
    },
    {
      header: "Client Secret",
      accessorKey: "clientSecret",
    },
    {
      header: "",
      id: "rotate",
      cell: (props) => (
        <div style={{ textAlign: "right" }}>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="rotate-tip">Rotate the client secret</Tooltip>
            }
          >
            <Button
              variant="outline-secondary"
              size="sm"
              aria-label="Rotate the client secret"
              onClick={() => setRotateTarget(props.row.original)}
            >
              <FontAwesomeIcon icon={faArrowsRotate} />
            </Button>
          </OverlayTrigger>
        </div>
      ),
    },
  ];

  const tableOrEmpty = isLoading ? (
    <SmallSpinner />
  ) : credentials.length === 0 ? (
    <p style={{ marginTop: "16px" }}>
      No client credentials yet for this service account.
    </p>
  ) : (
    <DataTable
      data={credentials}
      columns={columns}
      selectable={true}
      selectedIds={selectedIds}
      setSelectedIds={setSelectedIds}
      striped
      bordered
      hover
    />
  );

  const initialFormValues =
    form?.mode === "edit"
      ? {
          name: form.credential.name,
          redirectUri: form.credential.redirectUri,
        }
      : undefined;

  return (
    <div className="clientCredentials">
      {handleAlert(
        showFailAlert,
        errorMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "12px",
        }}
      >
        <Button
          className="squareGreenButton"
          onClick={() => setForm({ mode: "create" })}
        >
          New Credential
        </Button>
      </div>
      {selectedIds.length > 0 && (
        <Row className="mb-2">
          <Col sm={3}>
            <Button
              className="deleteBox"
              onClick={() => setShowDeleteConfirm(true)}
            >
              {selectedIds.length} Selected{" "}
              <span className="selectedSpan"></span>
              <FontAwesomeIcon style={{ color: "black" }} icon={faTrashCan} />
            </Button>
          </Col>
        </Row>
      )}
      {tableOrEmpty}
      <CredentialFormModal
        show={form !== null}
        mode={form?.mode ?? "create"}
        initialValues={initialFormValues}
        onClose={() => setForm(null)}
        onSubmit={handleSubmitForm}
        isSubmitting={isCreating || isUpdating}
      />
      <RotateConfirmModal
        show={rotateTarget !== null}
        credentialName={rotateTarget?.name ?? ""}
        onClose={() => setRotateTarget(null)}
        onConfirm={handleConfirmRotate}
        isRotating={isRotating}
      />
      <DeleteConfirmModal
        show={showDeleteConfirm}
        credentialNames={selectedCredentials.map((c) => c.name)}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
      <RevealCredentialsModal
        show={reveal !== null}
        onClose={handleCloseReveal}
        clientId={reveal?.clientId}
        clientSecret={reveal?.clientSecret}
        clientIdMasked={reveal?.clientIdMasked}
      />
    </div>
  );
};

export default ClientCredentials;
